import { useEffect, useRef, useState } from 'react';
import type { CurrentUser } from '../lib/types';
import { useTheme, type Theme } from '../lib/theme';
import { ChartIcon, HelpIcon, MonitorIcon, MoonIcon, SignOutIcon, SunIcon } from './Icons';

// One row that cycles rather than three rows to choose between: the choice is a
// 3-state toggle, and clicking the same spot repeatedly is faster than reading a
// list every time. Same behavior and labels as AP Central and Asset Central.
const THEME_CYCLE = [
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  { value: 'system', label: 'Auto', Icon: MonitorIcon },
] as const satisfies ReadonlyArray<{ value: Theme; label: string; Icon: typeof SunIcon }>;

interface AccountMenuProps {
  user: CurrentUser;
  hasPhoto: boolean;
  authDisabled: boolean;
  onSignOut: () => void;
  onHelp: () => void;
  /** Only passed when the signed-in person is a usage admin. */
  onUsage?: (() => void) | undefined;
}

export function AccountMenu({
  user,
  hasPhoto,
  authDisabled,
  onSignOut,
  onHelp,
  onUsage,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();

  const themeIndex = Math.max(
    0,
    THEME_CYCLE.findIndex((option) => option.value === theme),
  );
  const current = THEME_CYCLE[themeIndex]!;
  const next = THEME_CYCLE[(themeIndex + 1) % THEME_CYCLE.length]!;
  const CurrentThemeIcon = current.Icon;

  // Click-outside and Escape, registered only while the menu is open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const initials =
    user.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?';

  return (
    <div className="account" ref={containerRef}>
      <button
        type="button"
        className="account__button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {hasPhoto && !photoFailed ? (
          // Served by /api/profile-photo from the session, so no id appears in
          // the URL. If it 404s mid-session we quietly fall back to initials.
          <img src="/api/profile-photo" alt="" onError={() => setPhotoFailed(true)} />
        ) : (
          initials
        )}
      </button>

      {open && (
        <div className="account__menu" role="menu">
          <div className="account__identity">
            <div className="account__name">{user.name}</div>
            <div className="account__email">
              {authDisabled ? 'authentication disabled' : user.email}
            </div>
          </div>

          <div className="account__section">
            {/* Stays open on click so the next click lands on the same row. */}
            <button
              type="button"
              role="menuitem"
              className="account__item"
              onClick={() => setTheme(next.value)}
              title={`Switch to ${next.label}`}
            >
              <CurrentThemeIcon />
              <span className="account__item-label">Theme</span>
              <span className="account__item-value">{current.label}</span>
            </button>
          </div>

          <div className="account__section">
            <button
              type="button"
              role="menuitem"
              className="account__item"
              onClick={() => {
                setOpen(false);
                onHelp();
              }}
            >
              <HelpIcon />
              Help
            </button>
            {onUsage ? (
              <button
                className="account__item"
                onClick={() => {
                  setOpen(false);
                  onUsage();
                }}
              >
                <ChartIcon />
                Usage
              </button>
            ) : null}
          </div>

          <div className="account__section">
            <button
              type="button"
              role="menuitem"
              className="account__item account__item--danger"
              onClick={onSignOut}
            >
              <SignOutIcon />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
